import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { patchJoinPhotos } from '../lib/demoPhotos';

export function useHiringManager() {
  const { user, profile } = useAuth();
  const userId = user?.id;
  const currentCompany = profile?.current_company;
  const [fastTrackQueue, setFastTrackQueue] = useState([]);
  const [connectedCandidates, setConnectedCandidates] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch jobs this HM owns
  const fetchMyJobs = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .eq('hiring_manager_id', userId)
      .eq('is_active', true)
      .order('legacy_id');
    if (data) setMyJobs(data);
  }, [userId]);

  // Fetch vouched candidates (FastTrack queue)
  const fetchFastTrackQueue = useCallback(async () => {
    if (!userId) return;
    if (fastTrackQueue.length === 0 && connectedCandidates.length === 0) setLoading(true);

    // Get my job IDs
    const { data: jobs } = await supabase
      .from('jobs')
      .select('id')
      .eq('hiring_manager_id', userId)
      .eq('is_active', true);

    if (!jobs || jobs.length === 0) {
      setFastTrackQueue([]);
      setLoading(false);
      return;
    }

    const jobIds = jobs.map(j => j.id);

    // Get vouched interests for my jobs
    const { data: vouched } = await supabase
      .from('vouches')
      .select(`
        *,
        jobs:job_id (*),
        candidate:candidate_id (id, display_name, photo_url, headline, current_company, current_title, location),
        voucher:voucher_id (id, display_name, photo_url, headline, current_company),
        job_interest:job_interest_id (id, status, outcome, outcome_at, connected_at, candidate_first_response_at)
      `)
      .in('job_id', jobIds)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    const all = patchJoinPhotos(vouched || []);
    // Pending = waiting for HM review
    setFastTrackQueue(all.filter(v => v.job_interest?.status === 'vouched'));
    // Connected = HM already accepted
    setConnectedCandidates(all.filter(v => v.job_interest?.status === 'connected'));
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetchMyJobs();
    fetchFastTrackQueue();
  }, [fetchMyJobs, fetchFastTrackQueue]);

  async function connectWithCandidate(jobInterestId) {
    if (!user) return;
    const { error } = await supabase
      .from('job_interests')
      .update({ status: 'connected', updated_at: new Date().toISOString() })
      .eq('id', jobInterestId);
    if (!error) await fetchFastTrackQueue();
    return { error };
  }

  async function rejectCandidate(jobInterestId, reason, candidateId) {
    if (!user) return;
    const { error } = await supabase
      .from('job_interests')
      .update({ status: 'rejected', updated_at: new Date().toISOString() })
      .eq('id', jobInterestId);

    // Send rejection reason as a message
    if (!error && reason && candidateId) {
      await supabase.from('messages').insert({
        sender_id: user.id,
        recipient_id: candidateId,
        job_interest_id: jobInterestId,
        content: reason,
      });
    }

    if (!error) await fetchFastTrackQueue();
    return { error };
  }

  // Fetch unclaimed jobs matching this HM's company
  const fetchAvailableJobs = useCallback(async () => {
    if (!userId || !currentCompany) return;
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .is('hiring_manager_id', null)
      .eq('is_active', true)
      .eq('company', currentCompany)
      .order('legacy_id');
    if (data) setAvailableJobs(data);
  }, [userId, currentCompany]);

  // Claim selected jobs via RPC
  async function claimJobs(jobIds) {
    if (!user || !jobIds.length) return;
    const { error } = await supabase.rpc('claim_jobs', { p_job_ids: jobIds });
    if (!error) {
      await fetchMyJobs();
      await fetchAvailableJobs();
      await fetchFastTrackQueue();
    }
    return { error };
  }

  // Unclaim all jobs for this HM (for demo reset)
  async function unclaimMyJobs() {
    if (!user) return;
    const { error } = await supabase.rpc('unclaim_my_jobs');
    if (!error) {
      setMyJobs([]);
      await fetchAvailableJobs();
    }
    return { error };
  }

  // Subscribe to new vouches for this HM's jobs
  function subscribeToFastTrack(callback) {
    if (!user) return () => {};
    const channelName = `hm-vouches-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'vouches',
      }, (payload) => {
        callback(payload);
        fetchFastTrackQueue();
      })
      .subscribe((status, err) => {
        if (err && err.message?.includes('aborted')) return;
      });

    return () => {
      try { supabase.removeChannel(channel); } catch (_) {}
    };
  }

  async function closeOutcome(jobInterestId, outcome, outcomeNote) {
    if (!user) return { error: 'Not authenticated' };
    const { error } = await supabase.rpc('close_hiring_outcome', {
      p_job_interest_id: jobInterestId,
      p_outcome: outcome,
      p_outcome_note: outcomeNote || null,
    });
    if (!error) await fetchFastTrackQueue();
    return { error: error?.message };
  }

  async function updateJobMetadata(jobId, metadata) {
    if (!user) return { error: 'Not authenticated' };
    const { error } = await supabase.rpc('update_job_metadata', {
      p_job_id: jobId,
      p_role_family: metadata.role_family || null,
      p_seniority_band: metadata.seniority_band || null,
      p_required_skills: metadata.required_skills || [],
      p_preferred_skills: metadata.preferred_skills || [],
      p_remote_allowed: metadata.remote_allowed ?? true,
    });
    if (!error) await fetchMyJobs();
    return { error: error?.message };
  }

  return {
    fastTrackQueue,
    connectedCandidates,
    myJobs,
    availableJobs,
    loading,
    fetchMyJobs,
    fetchAvailableJobs,
    fetchFastTrackQueue,
    claimJobs,
    unclaimMyJobs,
    connectWithCandidate,
    rejectCandidate,
    closeOutcome,
    subscribeToFastTrack,
    updateJobMetadata,
  };
}
